import { WalkthroughStep } from "../../../common/components/walkthroughV3/useWalkthrough";

export const dashboardSteps: WalkthroughStep[] = [
  {
    title: {
      en: "Dashboard",
      tl: "Dashboard",
    },
    caption: {
      en: "This is your command center — it gives you a real-time view of product usage, subscriptions, and geographic distribution across all registered facilities and clients.",
      tl: "Ito ang iyong command center — nagbibigay ito ng real-time na view ng paggamit ng produkto, mga subscription, at heograpikong pamamahagi sa lahat ng nairehistrong pasilidad at kliyente.",
    },
    highlights: [{ id: "main-menu-dashboard" }],
    group: "Overview",
    captionPosition: "right",
  },
  {
    title: {
      en: "Date Filter",
      tl: "Date Filter",
    },
    caption: {
      en: "Use the **Date Filter** to change the time range for the charts. Choose from preset ranges like Today, Last Week, Last Month, or set a custom range.",
      tl: "Gamitin ang **Date Filter** para baguhin ang saklaw ng oras para sa mga chart. Pumili mula sa mga preset na saklaw tulad ng Today, Last Week, Last Month, o magtakda ng custom na saklaw.",
    },
    highlights: [{ id: "dashboard-date-filter-menu" }],
    group: "Overview",
    isButtonOrSearchBar: true,
    skipAutoAdvance: true,
    subCaption: [
      {
        title: {
          en: "Available ranges:",
          tl: "Mga available na saklaw:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Today, Last Week, Last Month** — Quick presets for common views.",
              tl: "**Today, Last Week, Last Month** — Mabilis na mga preset para sa karaniwang view.",
            },
          },
          {
            subTitle: {
              en: "**3 Months Ago, 6 Months Ago, A Year Ago** — Broader historical view.",
              tl: "**3 Months Ago, 6 Months Ago, A Year Ago** — Mas malawak na makasaysayang view.",
            },
          },
          {
            subTitle: {
              en: "**Custom** — Pick any specific start and end date.",
              tl: "**Custom** — Pumili ng anumang partikular na simula at katapusang petsa.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Total Product Usage Rate",
      tl: "Kabuuang Rate ng Paggamit ng Produkto",
    },
    caption: {
      en: "This chart shows how much each product is being used based on **total transactions**. The longer the bar, the more transactions that product has. Hover over a bar to see detailed stats.",
      tl: "Ipinapakita ng chart na ito kung gaano karaming ginagamit ang bawat produkto batay sa **kabuuang mga transaksyon**. Mas mahaba ang bar, mas maraming transaksyon ang produktong iyon. I-hover ang isang bar para makita ang detalyadong stats.",
    },
    highlights: [{ id: "dashboard-usage-rate-card" }],
    group: "Charts",
    captionPosition: "right",
    subCaption: [
      {
        title: {
          en: "How to read it:",
          tl: "Paano ito basahin:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Bar length** — Proportional to total transactions (log scale for large differences).",
              tl: "**Haba ng bar** — Proporsyonal sa kabuuang mga transaksyon (log scale para sa malalaking pagkakaiba).",
            },
          },
          {
            subTitle: {
              en: "**Hover tooltip** — Shows product name, transaction count, market share, and rank.",
              tl: "**Hover tooltip** — Nagpapakita ng pangalan ng produkto, bilang ng transaksyon, market share, at ranggo.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Total Subscriptions",
      tl: "Kabuuang Mga Subscription",
    },
    caption: {
      en: "This chart shows **Facilities** (dark blue) and **Clients** (light blue) subscribed to each product. Hover the bars or the product icon below to see exact counts.",
      tl: "Ipinapakita ng chart na ito ang **Mga Pasilidad** (madilim na asul) at **Mga Kliyente** (maliwanag na asul) na naka-subscribe sa bawat produkto. I-hover ang mga bar o ang product icon sa ibaba para makita ang eksaktong bilang.",
    },
    highlights: [{ id: "dashboard-subscriptions-card" }],
    group: "Charts",
    captionPosition: "left",
    subCaption: [
      {
        title: {
          en: "Legend:",
          tl: "Alamat:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Dark blue bar (top)** — Number of facilities subscribed to that product.",
              tl: "**Madilim na asul na bar (itaas)** — Bilang ng mga pasilidad na naka-subscribe sa produktong iyon.",
            },
          },
          {
            subTitle: {
              en: "**Light blue bar (bottom)** — Number of clients subscribed to that product.",
              tl: "**Maliwanag na asul na bar (ibaba)** — Bilang ng mga kliyente na naka-subscribe sa produktong iyon.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Geographic Distribution Dashboard",
      tl: "Dashboard ng Heograpikong Pamamahagi",
    },
    caption: {
      en: "This section shows where facilities are located across the Philippines — on a **map** and a **Top 10 Regions** table side by side.",
      tl: "Ipinapakita ng seksyong ito kung nasaan ang mga pasilidad sa buong Pilipinas — sa isang **mapa** at isang **Top 10 Regions** na talahanayan nang magkatabi.",
    },
    highlights: [{ id: "dashboard-geographic-card" }],
    group: "Geographic",
    captionPosition: "top",
  },
  {
    title: {
      en: "Product Filter",
      tl: "Product Filter",
    },
    caption: {
      en: "Use this dropdown to filter the map by product. Selecting a product updates the map markers to show only facilities assigned to that specific product.",
      tl: "Gamitin ang dropdown na ito para i-filter ang mapa ayon sa produkto. Ang pagpili ng produkto ay nag-a-update ng mga marker sa mapa upang ipakita lamang ang mga pasilidad na itinalaga sa partikular na produktong iyon.",
    },
    highlights: [{ selector: ".wt-product-filter-paper" }],
    group: "Geographic",
    isButtonOrSearchBar: true,
    skipAutoAdvance: true,
    subCaption: [
      {
        title: {
          en: "Filter options:",
          tl: "Mga opsyon sa filter:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**All Products** — Show all facilities regardless of product.",
              tl: "**All Products** — Ipakita ang lahat ng pasilidad anuman ang produkto.",
            },
          },
          {
            subTitle: {
              en: "**Specific product** — Show only facilities using that product.",
              tl: "**Tiyak na produkto** — Ipakita lamang ang mga pasilidad na gumagamit ng produktong iyon.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Philippines Map",
      tl: "Mapa ng Pilipinas",
    },
    caption: {
      en: "The map shows **clustered bubbles** where facilities are concentrated. Larger bubbles mean more facilities in that area. Click or zoom in to explore specific locations.",
      tl: "Ipinapakita ng mapa ang mga **clustered bubble** kung saan naka-konsentrate ang mga pasilidad. Mas malaking bubble ang nangangahulugang mas maraming pasilidad sa lugar na iyon. Mag-click o mag-zoom in para tuklasin ang mga partikular na lokasyon.",
    },
    highlights: [{ id: "dashboard-map" }],
    group: "Geographic",
    captionPosition: "right",
  },
  {
    title: {
      en: "Top 10 Regions",
      tl: "Top 10 Rehiyon",
    },
    caption: {
      en: "This table ranks the **top 10 regions** by total facilities. Hover over any row to see a detailed breakdown of product distribution for that region.",
      tl: "Iniraranggo ng talahanayan na ito ang **top 10 rehiyon** ayon sa kabuuang mga pasilidad. I-hover ang anumang row para makita ang detalyadong breakdown ng pamamahagi ng produkto para sa rehiyong iyon.",
    },
    highlights: [{ id: "dashboard-top-regions" }],
    group: "Geographic",
    captionPosition: "left",
    subCaption: [
      {
        title: {
          en: "Columns at a glance:",
          tl: "Mga Kolum:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Region** — Region name with growth indicator (↑) if trending up.",
              tl: "**Region** — Pangalan ng rehiyon na may growth indicator (↑) kung tumataas.",
            },
          },
          {
            subTitle: {
              en: "**Total Facilities** — Number of registered facilities in that region.",
              tl: "**Total Facilities** — Bilang ng mga nairehistrong pasilidad sa rehiyong iyon.",
            },
          },
          {
            subTitle: {
              en: "**Top Product** — The most-used product in that region.",
              tl: "**Top Product** — Ang pinaka-ginagamit na produkto sa rehiyong iyon.",
            },
          },
          {
            subTitle: {
              en: "**Product Distribution** — Product icons showing the mix of products used.",
              tl: "**Product Distribution** — Mga product icon na nagpapakita ng halo ng mga produktong ginagamit.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Walkthrough Icon",
      tl: "Icon ng Walkthrough",
    },
    caption: {
      en: "Tip: If you accidentally close this walkthrough, just click the **💡 icon** anytime to start it again from the **beginning**.",
      tl: "Tip: Kung aksidenteng nasara ang walkthrough, i-click lang ang **💡 icon** anumang oras upang simulan ito muli mula sa **simula**.",
    },
    highlights: [{ id: "wt-dashboard-button" }],
    captionPosition: "left",
    isButtonOrSearchBar: true,
    group: "Tips",
  },
];
